import { createRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import IconButton from '../../components/common/Buttons/IconButton';
import { useCommonStore } from '../../stores/commonStore';
import { uploadMedia } from '../../utils/api/media';
import { THREED_UPLOAD } from '../../utils/constants/images';
import { validateFiles, TAnyFileCollection, GLTFValidationReport, uploadFiles, ValidationErrorType, FileValidationReport } from '../../utils/file';
import { useVendorStore } from '../../stores/vendorStore';
import { VendorRoutes } from '../../utils/constants/routes';

interface QueryParams {
    id: string;
}

const ModelUploader = () => {
    // Back button
    const history = useHistory();
    const handleGoBack = () => {
        history.goBack();
    };

    // Invisible input html element
    const virtualInputRef = createRef<HTMLInputElement>();
    const handleVirtualInputClick = () => {
        virtualInputRef.current?.click();
    };
    const handleOnChange = async (event) => {
        event.preventDefault();
        handleFiles(virtualInputRef.current?.files);
    };

    // Drop events
    const handleOnDragOver = (event) => {
        event.preventDefault();
    };
    const handleOnDrop = async (event) => {
        event.preventDefault();
        handleFiles(event.dataTransfer.files);
    };
    const setUploadedModels = useVendorStore((state) => state.setUploadedModels);
    const setLoading = useCommonStore((state) => state.setLoading);

    const [validationReport, setValidationReport] = useState<GLTFValidationReport | undefined>(undefined);
    const { id } = useParams<QueryParams>();

    // Common between drop and input select
    const handleFiles = async (files: TAnyFileCollection) => {
        if (!files || !files.length) return;
        setValidationReport(undefined);
        try {
            setLoading(true);
            const validatedFilesMapping: PromiseSettledResult<FileValidationReport>[] = await validateFiles(files);

            setUploadedModels(validatedFilesMapping);
            await uploadFiles(files, id, validatedFilesMapping);

            history.push(VendorRoutes.UPLOAD_STATUS);

            setLoading(false);
        } catch (error: any) {
            let errorType: any = ValidationErrorType.SOMETHING_WENT_WRONG;
            if (typeof error === 'string') {
                errorType = error;
            } else if (error?.response?.status < 500) {
                errorType = error?.response?.data?.message;
            }
            setValidationReport({ valid: false, errorType });
            setLoading(false);
        }
    };

    const errorMessage = validationReport?.errorType;
    return (
        <>
            <div className="h-full">
                <div onDrop={handleOnDrop} onDragOver={handleOnDragOver} className="h-full mx-auto p-8 flex items-center justify-center flex-col relative">
                    <input type="file" onChange={handleOnChange} ref={virtualInputRef} className="hidden" accept=".glb" multiple></input>
                    <div className="absolute left-10 top-10">
                        <IconButton label="Go Back" onClick={handleGoBack} color="" borderColor="gray" textColor="gray">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.3327 5.33317H3.21935L6.94602 1.6065L5.99935 0.666504L0.666016 5.99984L5.99935 11.3332L6.93935 10.3932L3.21935 6.6665H11.3327V5.33317Z" fill="#111112" />
                            </svg>
                        </IconButton>
                    </div>
                    <div onClick={handleVirtualInputClick} className="lg:w-1/2 sm:w-full text-center custom-dashed-border lg:p-36 md:p-20 p-10 relative cursor-pointer">
                        <div className="absolute left-0 right-0 top-0 bottom-0 z-10" />
                        <img src={THREED_UPLOAD} className="block mx-auto" width={125} alt="" />
                        <h5 className="text-xl font-medium text-black mt-5">
                            Drag and Drop an .GLB files, or <span className="text-blue-600">Browse</span>
                        </h5>
                        <p className="text-base mt-1 text-gray-400">Ensure they follow the correct formats & structures, here’s a checklist</p>
                    </div>
                </div>
                {errorMessage && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 20,
                            left: '52%',
                            transform: 'translateX(-50%)',
                            width: 421,
                            height: 55,
                            background: '#0F0D21',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'left'
                        }}
                    >
                        <svg className="mx-4" width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="16" cy="16.5" r="16" fill="#8D3A37" />
                            <circle cx="16" cy="16.5" r="12" fill="#EC625C" />
                            <rect x="19.9751" y="11" width="2.4" height="13.3999" rx="1.2" transform="rotate(45 19.9751 11)" fill="white" />
                            <rect x="10.5005" y="12.6973" width="2.4" height="13.3999" rx="1.2" transform="rotate(-45 10.5005 12.6973)" fill="white" />
                        </svg>
                        <p className="text-white">{errorMessage}</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default ModelUploader;
